import { enqueueSnackbar } from 'notistack';
import { useState, useEffect, useCallback } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  alpha,
  Box,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  ListItemText,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';

import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';
import { fDate, fDateTime, fAPIDate } from 'src/utils/format-time';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  getIssuedChequeById,
  getIssuedChequePayments,
  deleteIssuedChequePayment,
} from 'src/redux/slices/issued-cheque';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import CopyableDetail from 'src/components/copyable-detail';
import { ConfirmDialog } from 'src/components/custom-dialog';

import {
  IIssuedChequeItem,
  IIssuedChequePayment,
  IssuedChequeStatus,
  ISSUED_CHEQUE_PAYABLE_STATUSES,
  getIssuedChequeStatusColor,
} from 'src/types/issued-cheque';

import IssuedChequeStatusDialog from './issued-cheque-status-dialog';
import IssuedChequePaymentDialog from './issued-cheque-payment-dialog';

type Props = {
  cheque: IIssuedChequeItem;
};

const userName = (user?: { firstName?: string; lastName?: string } | null) =>
  user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '-';

export default function IssuedChequeDetails({ cheque }: Props) {
  const router = useRouter();
  const dispatch = useAppDispatcher();
  const { user: authUser } = useAuthContext();

  const payments: IIssuedChequePayment[] = useAppSelector(
    (state) => state.issuedCheque.payments.items
  );

  const [tab, setTab] = useState('overview');
  const [paymentToDelete, setPaymentToDelete] = useState<IIssuedChequePayment | null>(null);

  const statusDialog = useBoolean();
  const paymentDialog = useBoolean();

  const canUpdate = hasPermission(
    authUser,
    'any',
    AuthPermissions.UPDATE_ISSUED_CHEQUE,
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const canUpdateStatus = hasPermission(
    authUser,
    'any',
    AuthPermissions.UPDATE_ISSUED_CHEQUE_STATUS,
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const isIssued = cheque.status === IssuedChequeStatus.ISSUED;
  const remainingInCents = cheque.amountInCents - cheque.paidInCents;
  const showPayments =
    ISSUED_CHEQUE_PAYABLE_STATUSES.includes(cheque.status) ||
    cheque.status === IssuedChequeStatus.PAID;

  const refresh = useCallback(() => {
    dispatch(getIssuedChequeById({ id: cheque._id }));
    dispatch(getIssuedChequePayments({ id: cheque._id }));
  }, [dispatch, cheque._id]);

  useEffect(() => {
    if (showPayments) {
      dispatch(getIssuedChequePayments({ id: cheque._id }));
    }
  }, [dispatch, cheque._id, showPayments]);

  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;

    try {
      await dispatch(
        deleteIssuedChequePayment({ id: cheque._id, paymentId: paymentToDelete._id })
      ).unwrap();
      enqueueSnackbar('Payment removed');
      refresh();
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message || 'Failed to remove the payment', {
        variant: 'error',
      });
    } finally {
      setPaymentToDelete(null);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Overview' },
    { value: 'history', label: 'History' },
    ...(showPayments ? [{ value: 'payments', label: 'Payments' }] : []),
  ];

  return (
    <>
      <Card sx={{ p: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{
            px: 2.5,
            boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
          }}
        >
          {tabs.map((item) => (
            <Tab key={item.value} value={item.value} label={item.label} />
          ))}
        </Tabs>

        {tab === 'overview' && (
          <Grid container spacing={2} mt={2}>
            <Grid xs={12} mb={1} display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1">Cheque Information</Typography>

              {canUpdate && isIssued && (
                <Button
                  size="small"
                  startIcon={<Iconify icon="solar:pen-bold" />}
                  onClick={() => router.push(paths.dashboard.issuedCheque.edit(cheque._id))}
                >
                  Edit
                </Button>
              )}
            </Grid>

            <Grid xs={12} md={3}>
              <CopyableDetail label="Bank" value={cheque.bank} />
            </Grid>

            <Grid xs={12} md={3}>
              <CopyableDetail label="Cheque Number" value={cheque.chequeNumber} />
            </Grid>

            <Grid xs={12} md={3}>
              <CopyableDetail label="Cheque Date" value={fAPIDate(cheque.chequeDate)} />
            </Grid>

            <Grid xs={12} md={3}>
              <CopyableDetail label="Amount" value={fCurrencyRupees(cheque.amountInCents)} />
            </Grid>

            <Grid xs={12} md={3}>
              <CopyableDetail label="Payee" value={cheque.payee} />
            </Grid>

            <Grid xs={12} md={3}>
              <CopyableDetail label="Type" value={cheque.type} />
            </Grid>

            <Grid xs={12} md={3}>
              <ListItemText
                primary="Status"
                secondary={
                  <Label variant="soft" color={getIssuedChequeStatusColor(cheque.status)}>
                    {cheque.status}
                  </Label>
                }
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            <Grid xs={12} md={3}>
              <ListItemText
                primary="Entered By"
                secondary={`${userName(cheque.createdBy)} · ${fDateTime(cheque.createdAt)}`}
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            <Grid xs={12}>
              <CopyableDetail
                label="Note"
                value={cheque.note || '-'}
                valueSx={{ whiteSpace: 'pre-wrap' }}
              />
            </Grid>

            {showPayments && (
              <>
                <Grid xs={12}>
                  <Divider sx={{ borderStyle: 'dashed', my: 1 }} />
                </Grid>

                <Grid xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Paid to the payee
                  </Typography>

                  <Stack direction="row" gap={4} flexWrap="wrap" mb={1}>
                    <ListItemText
                      primary="Paid"
                      secondary={fCurrencyRupees(cheque.paidInCents)}
                      primaryTypographyProps={{ typography: 'body2' }}
                      secondaryTypographyProps={{ component: 'span', color: 'success.main' }}
                    />

                    <ListItemText
                      primary="Remaining"
                      secondary={fCurrencyRupees(remainingInCents)}
                      primaryTypographyProps={{ typography: 'body2' }}
                      secondaryTypographyProps={{ component: 'span', color: 'warning.main' }}
                    />
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (cheque.paidInCents / cheque.amountInCents) * 100)}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        )}

        {tab === 'history' && (
          <Stack spacing={2} mt={3}>
            {[...cheque.history].reverse().map((entry) => (
              <Stack
                key={`${entry.status}-${entry.at}`}
                direction="row"
                alignItems="flex-start"
                gap={2}
              >
                <Label variant="soft" color={getIssuedChequeStatusColor(entry.status)}>
                  {entry.status}
                </Label>

                <Box>
                  <Typography variant="body2">{fDate(entry.date)}</Typography>

                  {entry.note && (
                    <Typography variant="caption" color="text.secondary">
                      {entry.note}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.disabled" display="block">
                    {userName(entry.by)} · {fDateTime(entry.at)}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        )}

        {tab === 'payments' && (
          <Box mt={3}>
            {canUpdateStatus && ISSUED_CHEQUE_PAYABLE_STATUSES.includes(cheque.status) && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                  onClick={paymentDialog.onTrue}
                >
                  Add Payment
                </Button>
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Payment History ({payments.length})
            </Typography>

            {payments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No payments recorded yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Recorded By</TableCell>
                    {canUpdateStatus && <TableCell />}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment._id}>
                      <TableCell>{fDate(payment.paymentDate)}</TableCell>
                      <TableCell align="right">{fCurrencyRupees(payment.amountInCents)}</TableCell>
                      <TableCell>{payment.note || '-'}</TableCell>
                      <TableCell>{userName(payment.createdBy)}</TableCell>
                      {canUpdateStatus && (
                        <TableCell align="right">
                          <Tooltip title="Remove payment">
                            <IconButton color="error" onClick={() => setPaymentToDelete(payment)}>
                              <Iconify icon="solar:trash-bin-trash-bold" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        <Stack direction="row" justifyContent="flex-end" gap={2} mt={4}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => router.push(paths.dashboard.issuedCheque.root)}
          >
            Back
          </Button>

          {canUpdateStatus && isIssued && (
            <Button variant="contained" onClick={statusDialog.onTrue}>
              Change Status
            </Button>
          )}

          {canUpdateStatus &&
            ISSUED_CHEQUE_PAYABLE_STATUSES.includes(cheque.status) &&
            remainingInCents > 0 && (
              <Button variant="contained" onClick={paymentDialog.onTrue}>
                Add Payment
              </Button>
            )}
        </Stack>
      </Card>

      {statusDialog.value && (
        <IssuedChequeStatusDialog
          cheque={cheque}
          isOpen={statusDialog.value}
          onClose={statusDialog.onFalse}
          onSuccess={refresh}
        />
      )}

      {paymentDialog.value && (
        <IssuedChequePaymentDialog
          cheque={cheque}
          isOpen={paymentDialog.value}
          onClose={paymentDialog.onFalse}
          onSuccess={() => {
            setTab('payments');
            refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={!!paymentToDelete}
        onClose={() => setPaymentToDelete(null)}
        title="Remove payment"
        content={
          paymentToDelete
            ? `Remove the ${fCurrencyRupees(paymentToDelete.amountInCents)} payment of ${fDate(
                paymentToDelete.paymentDate
              )}? The cheque status will be updated to match.`
            : ''
        }
        action={
          <Button variant="contained" color="error" onClick={handleDeletePayment}>
            Remove
          </Button>
        }
      />
    </>
  );
}
